// Spec 053 Flow B — refund fault attribution (billing svc).

/** Who is responsible for the refund (BR-4). Required on every admin approval. */
export type FaultParty = 'GARAGE' | 'PLATFORM' | 'CUSTOMER' | 'NONE';

/**
 * Who bears the non-refundable gateway fee (BR-8b / AC-11b). Default CUSTOMER.
 * Spec 067 — GARAGE: a Tabby payment whose fee the garage pays, refunded for the garage's fault
 * (the garage keeps bearing the fee; the customer is refunded in full). Billing decides it; the
 * admin never sends it.
 */
export type FeeBorneBy = 'CUSTOMER' | 'PLATFORM' | 'GARAGE';

/** How the consequence line words the processing fee for each bearer. */
export function feeBorneByPhrase(feeBorneBy: FeeBorneBy | string | null | undefined): string {
  if (feeBorneBy === 'PLATFORM') return 'refunded by Garagat';
  if (feeBorneBy === 'GARAGE') return 'paid by the garage';
  return 'deducted';
}

/**
 * Spec 068 — who bears a refunded service fee: Garagat (PLATFORM fault, system refunds) or the garage
 * (GARAGE fault: recharged at settlement). null when the fee is not refunded.
 */
export type ServiceFeeBorneBy = 'PLATFORM' | 'GARAGE';

/** How the consequence line and the checkbox word the bearer of a refunded service fee. */
export function serviceFeeBorneByPhrase(borneBy: ServiceFeeBorneBy | string | null | undefined): string | null {
  if (borneBy === 'PLATFORM') return 'borne by Garagat';
  if (borneBy === 'GARAGE') return 'recharged to the garage';
  return null;
}

/** Commission handling on the settlement side (BR-5 / BR-6). */
export type CommissionTreatment = 'RETAIN' | 'REVERSE_PROPORTIONAL';

export const FAULT_PARTY_LABELS: Record<FaultParty, string> = {
  GARAGE: 'Garage',
  PLATFORM: 'Garagat',
  CUSTOMER: 'Customer',
  NONE: 'No service delivered',
};

export interface RefundPreviewParams {
  faultParty: FaultParty;
  /** Optional; the backend defaults to the ceiling (full refund). Always the SERVICE slice (BR-25). */
  amount?: number;
  feeBorneBy?: FeeBorneBy;
  commissionTreatment?: CommissionTreatment;
  /**
   * Spec 053 Phase 3 (BR-25 / AC-40) — also reverse Garagat's own pickup & delivery fee.
   * Omitted rather than sent as `false`, so a billing build without the parameter is unaffected.
   */
  refundDeliveryFee?: boolean;
  /**
   * Spec 068 §3.6 — refund Garagat's service fee too. Unlike the delivery flag this is tri-state:
   * omitted → billing applies the policy default for the fault party (PLATFORM/GARAGE refund it,
   * CUSTOMER/NONE keep it); true/false → the admin's explicit choice.
   */
  refundServiceFee?: boolean;
  /** #716 — the refund is part of a dispute CLOSE, which completes the order before refunding. */
  closing?: boolean;
}

/** GET /api/admin/refunds/{id}/preview — design.md §3 Billing. */
export interface RefundPreviewResponse {
  /** The SERVICE slice requested — never includes the delivery fee (Phase 3 delta D23). */
  amountRequested: number;
  /** Payment-wide cap (service + fee − gateway fee). Use `serviceCeiling` to validate the amount. */
  ceiling: number;
  customerReceives: number;
  feeAmount: number | null;
  feeBorneBy: FeeBorneBy;
  /** Gross slice the settlement claws back from the garage. */
  garageDebit: number;
  /** The garage's net hit: garageDebit − commissionReversed − vatOnCommissionReversed. */
  garageBears: number;
  platformExpense: number;
  commissionTreatment: CommissionTreatment;
  commissionReversed: number;
  vatOnCommissionReversed: number;
  garagePositionBefore: number | null;
  garagePositionAfter: number | null;
  projectedNegative: boolean;
  warning: string | null;
  /** false = the fee is still a schedule estimate, not the provider-reported one. */
  feeIsActual: boolean;

  // ── Spec 053 Phase 3 (delta D22/D23) — the delivery-fee split ──
  // Optional on the wire: a billing build from before S11 simply omits them, and every
  // consumer here degrades to "this order carries no delivery fee".
  /**
   * BR-25 / AC-40's 422 ceiling: `min(payment ceiling, service gross − already refunded service)`.
   * The payment-wide `ceiling` silently permits refunding the fee as service once the fee sits
   * inside `payment.amount`, so the amount field must be validated against THIS number.
   */
  serviceCeiling?: number | null;
  /** Garagat's pickup & delivery fee on the order, VAT-inclusive. 0/absent ⇒ no fee was charged. */
  deliveryFeeCharged?: number | null;
  /** The fee slice this refund would reverse — 0 unless `refundDeliveryFee` was requested. */
  deliveryFeeRefunded?: number | null;
  /**
   * #716 — true when the order has not completed (and this is not a dispute close): the refund is
   * paid from the money Garagat is still holding, so the garage bears nothing and no position is
   * projected. False after completion; null/absent when billing could not tell.
   */
  paidFromHeldFunds?: boolean | null;

  // ── Spec 068 §3.6 — the service-fee slice. 0.00/null/absent ⇒ no fee on the payment. ──
  /** Garagat's service fee charged on the payment, VAT-inclusive. */
  serviceFeeCharged?: number | null;
  /**
   * The fee slice this refund would return to the customer. With no `refundServiceFee` sent it is
   * the policy default, so `> 0` is what the checkbox starts ticked from.
   */
  serviceFeeRefunded?: number | null;
  serviceFeeBorneBy?: ServiceFeeBorneBy | null;
  /** The part charged back to the garage at settlement (= refunded when GARAGE bears it). */
  serviceFeeRecharge?: number | null;
}

/** The service ceiling to validate against, falling back to the payment-wide one. */
export function serviceCeilingOf(preview: RefundPreviewResponse | null, fallback: number | null): number | null {
  if (preview?.serviceCeiling != null) return preview.serviceCeiling;
  if (preview?.ceiling != null && (preview.deliveryFeeCharged ?? 0) === 0) return preview.ceiling;
  return fallback;
}

/** POST /api/admin/refunds/{id}/approve body (extended, Spec 053). */
export interface RefundApproveRequest {
  faultParty: FaultParty;
  /** The SERVICE slice (BR-25) — the delivery fee is requested with the flag below, never here. */
  amount?: number;
  feeBorneBy: FeeBorneBy;
  commissionTreatment?: CommissionTreatment;
  note?: string;
  /** Spec 053 BR-25 — also reverse Garagat's pickup & delivery fee (issues a PCN). */
  refundDeliveryFee?: boolean;
  /** Spec 068 — refund Garagat's service fee; omitted → the policy default for the fault party. */
  refundServiceFee?: boolean;
}

/** Mirrors billing AdminRefundResponse + the Spec 053 list additions (ageDays, attentionReason). */
export interface AdminRefundResponse {
  id: string;
  orderId: string | null;
  paymentId: string;
  refundType: string | null;
  amount: number;
  originalOrderAmount: number | null;
  reason: string | null;
  transactionRef: string | null;
  status: string;
  processedBy: string | null;
  processedAt: string | null;
  createdAt: string;
  ageDays?: number;
  attentionReason?: string | null;
  /** Spec 067 — the payment's provider ("stripe", "tabby", "MOCK"; compare case-insensitively). */
  provider?: string | null;
  /** Spec 067 — the provider's own payment id: the Tabby payment UUID, or Stripe's pi_…. */
  providerPaymentId?: string | null;
  feeBorneBy?: FeeBorneBy | null;
  /** Spec 067 DD3-A — the garage-borne Tabby fee (net of VAT) this refund takes back off the garage, and its VAT. */
  garageFeeReversal?: number | null;
  garageFeeReversalVat?: number | null;
  /** Spec 068 (#1053) — Garagat's service fee this refund returned (0 when none) and who bears it (null when none). */
  serviceFeeRefunded?: number | null;
  serviceFeeBorneBy?: ServiceFeeBorneBy | null;
}

export interface RefundListParams {
  status?: string;
  attention?: boolean;
}
